'use client';

import { useMemo } from 'react';
import { METRICS, type MetricId } from '../lib/metric-definitions';
import type { MetricData } from '../hooks/use-metrics-data';
import { fmtMoney, fmtCount, fmtDateShort } from '../lib/format';

interface Props {
  metrics: Record<string, MetricData>;
  activeMetrics: MetricId[];
  rangeLabel: string;
  compareLabel: string | null;
}

function buildNarrative(metrics: Record<string, MetricData>, activeMetrics: MetricId[], rangeLabel: string): string[] {
  const parts: string[] = [];
  const primary = activeMetrics[0];
  if (!primary || !metrics[primary]) return ['Nu există date suficiente pentru a genera sumar.'];

  const pm = metrics[primary];
  const def = METRICS[primary];
  const unit = def.unit === 'money' ? 'RON' : (primary === 'newClients' ? 'clienți' : 'facturi');
  const val = def.unit === 'money' ? fmtMoney(pm.current, false) : fmtCount(pm.current);

  const trend =
    pm.deltaPct > 5 ? 'creștere clară' :
    pm.deltaPct > 1 ? 'creștere ușoară' :
    pm.deltaPct < -5 ? 'scădere clară' :
    pm.deltaPct < -1 ? 'scădere ușoară' :
    'stabilitate';

  parts.push(
    'În ' + rangeLabel.toLowerCase() + ', ' + def.label.toLowerCase() + ' au însumat ' +
    val + ' ' + unit + (pm.deltaPct !== 0 ? ' (' + (pm.deltaPct > 0 ? '+' : '') + pm.deltaPct.toFixed(1) + '% față de perioada de comparație)' : '') + '.'
  );

  // Secondary metric
  if (activeMetrics[1] && metrics[activeMetrics[1]]) {
    const sm = metrics[activeMetrics[1]!]!;
    const sdef = METRICS[activeMetrics[1]];
    const sunit = sdef.unit === 'money' ? 'RON' : (activeMetrics[1] === 'newClients' ? 'clienți' : 'facturi');
    const sval = sdef.unit === 'money' ? fmtMoney(sm.current, false) : fmtCount(sm.current);
    parts.push(sdef.label + ' au fost ' + sval + ' ' + sunit + '.');
  }

  // Trend sentence
  parts.push('Indicator principal: ' + trend + ' pe ' + def.label.toLowerCase() + '.');
  return parts;
}

export function MonitorBackView({ metrics, activeMetrics, rangeLabel, compareLabel }: Props) {
  const seriesList = useMemo(() => {
    return activeMetrics
      .map((id) => ({ id, def: METRICS[id], data: metrics[id] }))
      .filter((s): s is { id: MetricId; def: typeof METRICS[MetricId]; data: MetricData } => !!s.data && !s.data.isSnapshot);
  }, [activeMetrics, metrics]);

  const narrative = useMemo(() => buildNarrative(metrics, activeMetrics, rangeLabel), [metrics, activeMetrics, rangeLabel]);

  // Bucketing for bar chart — show max 14 buckets (condense if > 14)
  const firstSeries = seriesList[0]?.data.series || [];
  const totalBuckets = firstSeries.length;
  const maxBars = 14;
  const condenseFactor = Math.max(1, Math.ceil(totalBuckets / maxBars));
  const condensedCount = Math.ceil(totalBuckets / condenseFactor);

  const groupedData = useMemo(() => {
    const result: { label: string; values: { id: MetricId; color: string; value: number }[] }[] = [];
    for (let bi = 0; bi < condensedCount; bi++) {
      const startIdx = bi * condenseFactor;
      const endIdx = Math.min(totalBuckets, startIdx + condenseFactor);
      const label = firstSeries[startIdx] ? fmtDateShort(firstSeries[startIdx].t) : '';
      const values = seriesList.map((s) => {
        let sum = 0;
        for (let i = startIdx; i < endIdx; i++) {
          sum += s.data.series[i]?.v || 0;
        }
        return { id: s.id, color: s.def.color, value: sum };
      });
      result.push({ label, values });
    }
    return result;
  }, [seriesList, condenseFactor, condensedCount, totalBuckets, firstSeries]);

  const maxValue = useMemo(() => {
    let max = 0;
    for (const b of groupedData) {
      for (const v of b.values) {
        if (v.value > max) max = v.value;
      }
    }
    return max || 1;
  }, [groupedData]);

  return (
    <div className="px-5 py-4 h-full flex flex-col gap-3">
      {/* Metric summary cards — row at top */}
      <div
        className="shrink-0 grid gap-2"
        style={{ gridTemplateColumns: `repeat(${activeMetrics.length}, minmax(0, 1fr))` }}
      >
        {activeMetrics.map((id) => {
          const d = metrics[id];
          const def = METRICS[id];
          if (!d || !def) return null;
          const unit = def.unit === "money" ? "RON" : (id === "newClients" ? "clienți" : "facturi");
          const val = def.unit === "money" ? fmtMoney(d.current, false) : fmtCount(d.current);
          const delta = d.deltaPct;
          const deltaCls =
            Math.abs(delta) < 1 ? "text-neutral-500" :
            delta > 0 ? "text-emerald-700 dark:text-emerald-400" :
            "text-red-700 dark:text-red-400";
          return (
            <div
              key={id}
              className="px-3 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg"
              style={{ borderLeftWidth: 3, borderLeftColor: def.color }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: def.color }} />
                <span className="text-[0.625rem] font-semibold uppercase tracking-[0.05em] text-neutral-600 dark:text-neutral-400 truncate">
                  {def.label}
                </span>
              </div>
              <div
                className="font-medium leading-none tracking-[-0.02em] text-neutral-900 dark:text-neutral-100"
                style={{ fontSize: '1.125rem', fontWeight: 600 }}
              >
                {val}
                <span className="ml-0.5 text-[0.5625rem] font-medium text-neutral-500 align-super">
                  {unit}
                </span>
              </div>
              {!d.isSnapshot ? (
                <div className={"mt-1 inline-flex items-center gap-0.5 text-[0.625rem] font-semibold " + deltaCls}>
                  {Math.abs(delta) >= 1 && (delta > 0 ? "▲" : "▼")}
                  {Math.abs(delta) < 1 ? "stabil" : (delta > 0 ? "+" : "") + delta.toFixed(1) + "%"}
                </div>
              ) : (
                <div className="mt-1 text-[0.5938rem] text-neutral-400 font-medium">snapshot curent</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Narrative — compact one-block */}
      <div className="shrink-0 px-4 py-2.5 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div
          className="text-[0.7813rem] leading-[1.45] text-neutral-700 dark:text-neutral-300"
        >
          {narrative.join(" ")}
        </div>
      </div>

      {/* Horizontal comparison for snapshot metrics */}
      {(() => {
        const snapshotMetrics = activeMetrics
          .map((id) => ({ id, def: METRICS[id], data: metrics[id] }))
          .filter((s) => !!s.data && s.data.isSnapshot);
        if (snapshotMetrics.length === 0) return null;
        const maxSnap = Math.max(1, ...snapshotMetrics.map((s) => s.data!.current));
        return (
          <div className="shrink-0 px-4 py-2.5 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <div className="text-[0.5938rem] font-bold uppercase tracking-[0.1em] text-neutral-500 mb-2">
              Snapshot curent
            </div>
            <div className="flex flex-col gap-1.5">
              {snapshotMetrics.map((s) => {
                const v = s.data!.current;
                const pct = (v / maxSnap) * 100;
                const unit = s.def.unit === "money" ? "RON" : (s.id === "newClients" ? "clienți" : "facturi");
                const valText = s.def.unit === "money" ? fmtMoney(v, false) : fmtCount(v);
                return (
                  <div key={s.id} className="flex items-center gap-2.5">
                    <div className="flex items-center gap-1.5 w-[120px] shrink-0">
                      <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.def.color }} />
                      <span className="text-[0.6875rem] font-medium text-neutral-700 dark:text-neutral-300 truncate">
                        {s.def.label}
                      </span>
                    </div>
                    <div className="flex-1 h-5 bg-neutral-100 dark:bg-neutral-900 rounded overflow-hidden relative">
                      <div
                        className="h-full rounded transition-all duration-500"
                        style={{ background: s.def.color, width: pct + "%", minWidth: "2px" }}
                      />
                    </div>
                    <div className="w-[95px] text-right shrink-0">
                      <span className="font-semibold text-[0.7188rem] text-neutral-900 dark:text-neutral-100">{valText}</span>
                      <span className="text-[0.5938rem] text-neutral-500 ml-0.5">{unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Grouped bar chart — fills remaining height */}
      {seriesList.length > 0 && (
        <div className="flex-1 min-h-0 flex flex-col px-4 py-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <div className="shrink-0 flex items-center justify-between gap-3 mb-2">
            <div className="text-[0.5938rem] font-bold uppercase tracking-[0.1em] text-neutral-500">
              Evoluție · coloane grupate
            </div>
            <div className="flex flex-wrap gap-2.5 text-[0.6563rem]">
              {seriesList.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.def.color }} />
                  <span className="font-medium text-neutral-700 dark:text-neutral-300">{s.def.label}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Bars — fill available height with %, not fixed px */}
          <div className="flex-1 min-h-0 flex items-end gap-1.5">
            {groupedData.map((bucket, bi) => (
              <div key={bi} className="flex-1 flex flex-col items-center gap-1 h-full min-w-0">
                <div className="w-full flex-1 min-h-0 flex items-end justify-center gap-[2px] border-b border-neutral-200 dark:border-neutral-700">
                  {bucket.values.map((v) => {
                    const ratio = maxValue > 0 && isFinite(v.value) ? v.value / maxValue : 0;
                    const isZero = v.value === 0;
                    const heightPct = Math.max(isZero ? 2 : 4, ratio * 100);
                    return (
                      <div
                        key={v.id}
                        className="flex-1 rounded-t-[2px] transition-all hover:opacity-80"
                        style={{
                          background: v.color,
                          height: heightPct + "%",
                          opacity: isZero ? 0.3 : 1,
                        }}
                        title={METRICS[v.id]?.label + ": " + (METRICS[v.id]?.unit === "money" ? fmtMoney(v.value, false) + " RON" : fmtCount(v.value))}
                      />
                    );
                  })}
                </div>
                <div className="shrink-0 text-[0.5313rem] text-neutral-500 whitespace-nowrap leading-none">{bucket.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {compareLabel && (
        <div className="shrink-0 text-[0.625rem] text-neutral-500 italic leading-none">
          * Comparat cu {compareLabel}
        </div>
      )}
    </div>
  );
}
